import React, { useState, useRef, useEffect } from 'react';
import { useCurrency, CURRENCIES } from '../../contexts/CurrencyContext';
import { ChevronDownIcon, CheckIcon, ArrowPathIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface CurrencyBadgeProps {
  compact?: boolean;
}

const CurrencyBadge: React.FC<CurrencyBadgeProps> = ({ compact = false }) => {
  const { currency, currencyInfo, updateCurrencyPreference, resetToAutoDetected, loading } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filteredCurrencies = CURRENCIES.filter(curr => 
    curr.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    curr.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCurrencySelect = async (currencyCode: string) => {
    await updateCurrencyPreference(currencyCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleReset = async () => {
    await resetToAutoDetected();
    setIsOpen(false);
    setSearchTerm('');
  };

  const flagEmoji = currencyInfo?.flag_emoji || '🇳🇬';
  const countryName = currencyInfo?.country_name || 'Nigeria';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`
          flex items-center gap-2 px-3 py-2 
          bg-gradient-to-r from-sky-50 to-blue-50 
          border border-sky-200 rounded-xl
          hover:from-sky-100 hover:to-blue-100 hover:border-sky-300
          focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1
          transition-all duration-200 ease-in-out
          ${loading ? 'opacity-70 cursor-wait' : 'cursor-pointer'}
          shadow-sm hover:shadow
        `}
        title={`${countryName} - ${currency.name}`}
      >
        <span className="text-xl leading-none" role="img" aria-label={countryName}>
          {flagEmoji}
        </span>
        
        <div className={`flex items-center gap-1 ${compact ? 'hidden sm:flex' : 'flex'}`}>
          <span className="font-bold text-sky-700 text-sm">
            {currency.symbol}
          </span>
          <span className="font-semibold text-sky-600 text-sm">
            {currency.code}
          </span>
        </div>

        {loading ? (
          <ArrowPathIcon className="h-4 w-4 text-sky-500 animate-spin" />
        ) : (
          <ChevronDownIcon className={`h-4 w-4 text-sky-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        )}

        {currencyInfo?.is_auto_detected && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" title="Auto-detected location"></span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-3 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-100">
            <div className="flex items-center gap-2 mb-2">
              <GlobeAltIcon className="h-5 w-5 text-sky-600" />
              <span className="font-bold text-gray-800 text-sm">Currency Settings</span>
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <span className="text-lg">{flagEmoji}</span>
                <span>{countryName}</span>
              </div>
              {currencyInfo?.is_auto_detected ? (
                <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  <span className="h-1.5 w-1.5 bg-green-500 rounded-full"></span>
                  Auto-detected
                </span>
              ) : (
                <button 
                  onClick={handleReset}
                  className="flex items-center gap-1 text-sky-600 hover:text-sky-700 bg-sky-50 hover:bg-sky-100 px-2 py-0.5 rounded-full transition-colors"
                >
                  <ArrowPathIcon className="h-3 w-3" />
                  Reset to auto
                </button>
              )}
            </div>
          </div>

          <div className="p-2">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search currency..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent bg-gray-50"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCurrencies.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No currencies found
              </div>
            ) : (
              filteredCurrencies.map((curr) => (
                <button
                  key={curr.code}
                  onClick={() => handleCurrencySelect(curr.code)}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 
                    hover:bg-sky-50 transition-colors
                    ${currency.code === curr.code ? 'bg-sky-50' : ''}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-700 w-8">
                      {curr.symbol}
                    </span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-800 text-sm">{curr.code}</div>
                      <div className="text-xs text-gray-500">{curr.name}</div>
                    </div>
                  </div>
                  
                  {currency.code === curr.code && (
                    <CheckIcon className="h-5 w-5 text-sky-600" />
                  )}
                </button>
              ))
            )}
          </div>

          <div className="p-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              Prices displayed in selected currency
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyBadge;
